import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { 
  Shield, 
  Download, 
  Search, 
  Filter, 
  UserPlus,
  UserMinus,
  UserCog,
  UserX,
  FileText,
  ArrowRightLeft,
  Clock,
  Activity
} from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from 'react-i18next';
import { SharedPagination } from "./shared";
import { LoadingOverlay } from "./LoadingOverlay";

// Map action types to icons and colors
const ACTION_STYLES = {
  official_registered: { icon: UserPlus, color: "bg-emerald-100 text-emerald-700", label: "Self-Registered" },
  official_created: { icon: UserPlus, color: "bg-blue-100 text-blue-700", label: "Created by Admin" },
  official_updated: { icon: UserCog, color: "bg-amber-100 text-amber-700", label: "Updated" },
  official_deleted: { icon: UserX, color: "bg-red-100 text-red-700", label: "Deleted" },
  official_enabled: { icon: UserPlus, color: "bg-emerald-100 text-emerald-700", label: "Enabled" },
  official_disabled: { icon: UserMinus, color: "bg-orange-100 text-orange-700", label: "Disabled" },
  application_assigned: { icon: FileText, color: "bg-blue-100 text-blue-700", label: "Assigned" },
  application_reassigned: { icon: ArrowRightLeft, color: "bg-purple-100 text-purple-700", label: "Reassigned" },
  application_status_changed: { icon: Activity, color: "bg-cyan-100 text-cyan-700", label: "Status Changed" },
  application_transferred: { icon: ArrowRightLeft, color: "bg-indigo-100 text-indigo-700", label: "Transferred" }
};

export default function AuditLogs() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [actionTypes, setActionTypes] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    totalPages: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    action: "",
    targetType: "",
    searchQuery: ""
  });
  const [showFilters, setShowFilters] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      
      if (filters.action) params.append("action", filters.action);
      if (filters.targetType) params.append("target_type", filters.targetType);
      
      const response = await api.get(`/audit-logs?${params}`);
      setLogs(response.data.logs || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (error) {
      toast.error(t('auditLogs.loadError') || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, t]);

  const loadSummary = async () => {
    try {
      const response = await api.get("/audit-logs/summary?days=30");
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to load summary:", error);
    }
  };

  const loadActionTypes = async () => {
    try {
      const response = await api.get("/audit-logs/action-types");
      setActionTypes(response.data.action_types || []);
    } catch (error) {
      console.error("Failed to load action types:", error);
    }
  };

  useEffect(() => {
    loadLogs();
    loadSummary();
    loadActionTypes();
  }, [loadLogs]);

  const handleExport = async (exportFormat) => {
    try {
      toast.info(t('auditLogs.exporting') || "Preparing export...");
      const params = new URLSearchParams({ format: exportFormat });
      if (filters.action) params.append("action", filters.action);
      if (filters.targetType) params.append("target_type", filters.targetType);
      
      const response = await api.get(`/audit-logs/export?${params}`, {
        responseType: 'blob'
      });
      
      const mimeType = exportFormat === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const filename = `audit_logs_${new Date().toISOString().slice(0,10)}.${exportFormat === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('auditLogs.exportSuccess') || "Export completed successfully");
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('auditLogs.exportError') || "Failed to export audit logs");
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!filters.searchQuery) return true;
    const query = filters.searchQuery.toLowerCase();
    return (
      log.performed_by_name?.toLowerCase().includes(query) ||
      log.target_name?.toLowerCase().includes(query) ||
      log.action?.toLowerCase().includes(query)
    );
  });

  const getActionStyle = (action) => {
    return ACTION_STYLES[action] || { icon: Activity, color: "bg-gray-100 text-gray-700", label: action };
  };

  // Format details for better readability
  const formatDetails = (details, action) => {
    if (!details || typeof details !== 'object') return null;
    
    const formatValue = (value) => {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (Array.isArray(value)) return value.join(', ');
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };

    const formatKey = (key) => {
      // Convert snake_case to Title Case
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    };

    // Special handling for different action types
    const renderChanges = () => {
      if (details.previous && details.new) {
        // For updates, show what changed
        const changedFields = [];
        Object.keys(details.new).forEach(key => {
          const oldVal = details.previous[key];
          const newVal = details.new[key];
          if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changedFields.push({ key, oldVal, newVal });
          }
        });
        
        if (changedFields.length === 0) return null;
        
        return (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-slate-700 mb-2">Changes Made:</div>
            {changedFields.map(({ key, oldVal, newVal }) => (
              <div key={key} className="bg-white p-2 rounded border border-slate-100">
                <div className="text-xs font-medium text-slate-600 mb-1">{formatKey(key)}</div>
                <div className="flex flex-wrap gap-2 items-center text-xs">
                  <span className="px-2 py-0.5 bg-red-50 text-red-600 rounded line-through">{formatValue(oldVal)}</span>
                  <span className="text-slate-400">→</span>
                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded font-medium">{formatValue(newVal)}</span>
                </div>
              </div>
            ))}
          </div>
        );
      }
      return null;
    };

    // Handle "fields_changed" format from backend
    if (details.fields_changed && Array.isArray(details.fields_changed)) {
      return (
        <div className="space-y-2">
          <div className="flex gap-2 text-xs">
            <span className="font-medium text-slate-600">Fields Changed:</span>
            <div className="flex flex-wrap gap-1">
              {details.fields_changed.map((field, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                  {formatKey(field)}
                </span>
              ))}
            </div>
          </div>
          {details.password_changed && (
            <div className="flex gap-2 text-xs">
              <span className="font-medium text-slate-600">Password Changed:</span>
              <span className="text-amber-600 font-semibold">Yes</span>
            </div>
          )}
        </div>
      );
    }

    const renderKeyValue = (obj, excludeKeys = []) => {
      const entries = Object.entries(obj)
        .filter(([key]) => !excludeKeys.includes(key) && !key.startsWith('_'));
      
      if (entries.length === 0) return null;
      
      return entries.map(([key, value]) => {
          // Skip complex nested objects for cleaner display
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            return null;
          }
          return (
            <div key={key} className="flex gap-2 text-xs py-1 border-b border-slate-100 last:border-0">
              <span className="font-medium text-slate-600 min-w-[120px]">{formatKey(key)}:</span>
              <span className="text-slate-900 break-words">{formatValue(value)}</span>
            </div>
          );
        }).filter(Boolean);
    };

    // Action-specific formatting
    if (action === 'official_updated' && details.previous && details.new) {
      return renderChanges();
    }

    if (action === 'official_deleted' && details.applications_reassigned !== undefined) {
      return (
        <div className="space-y-1">
          <div className="flex gap-2 text-xs">
            <span className="font-medium text-slate-600">Applications Reassigned:</span>
            <span className="text-amber-600 font-semibold">{details.applications_reassigned}</span>
          </div>
          {details.reason && (
            <div className="flex gap-2 text-xs">
              <span className="font-medium text-slate-600">Reason:</span>
              <span className="text-slate-900">{details.reason}</span>
            </div>
          )}
        </div>
      );
    }

    if ((action === 'application_assigned' || action === 'application_reassigned') && details.assigned_to) {
      return (
        <div className="space-y-1">
          <div className="flex gap-2 text-xs">
            <span className="font-medium text-slate-600">Assigned To:</span>
            <span className="text-emerald-600 font-semibold">{details.assigned_to}</span>
          </div>
          {details.previous_official && (
            <div className="flex gap-2 text-xs">
              <span className="font-medium text-slate-600">Previous:</span>
              <span className="text-slate-500">{details.previous_official}</span>
            </div>
          )}
        </div>
      );
    }

    if (action === 'application_status_changed') {
      return (
        <div className="space-y-1">
          {details.previous_status && (
            <div className="flex flex-wrap gap-1 items-center text-xs">
              <span className="font-medium text-slate-600">Status:</span>
              <span className="text-red-500">{formatKey(details.previous_status)}</span>
              <span className="text-slate-400">→</span>
              <span className="text-emerald-600 font-semibold">{formatKey(details.new_status || details.status)}</span>
            </div>
          )}
          {details.comment && (
            <div className="flex gap-2 text-xs mt-1">
              <span className="font-medium text-slate-600">Comment:</span>
              <span className="text-slate-700 italic">"{details.comment}"</span>
            </div>
          )}
        </div>
      );
    }

    if (action === 'application_transferred') {
      return (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-1 items-center text-xs">
            <span className="font-medium text-slate-600">Department:</span>
            <span className="text-slate-500">{formatKey(details.from_department || 'Unknown')}</span>
            <span className="text-slate-400">→</span>
            <span className="text-indigo-600 font-semibold">{formatKey(details.to_department || 'Unknown')}</span>
          </div>
          {details.assigned_to && (
            <div className="flex gap-2 text-xs">
              <span className="font-medium text-slate-600">Assigned To:</span>
              <span className="text-slate-900">{details.assigned_to}</span>
            </div>
          )}
        </div>
      );
    }

    if (action === 'official_created' || action === 'official_registered') {
      return (
        <div className="space-y-1">
          {details.department && (
            <div className="flex gap-2 text-xs">
              <span className="font-medium text-slate-600">Department:</span>
              <span className="text-slate-900 capitalize">{details.department}</span>
            </div>
          )}
          {details.designation && (
            <div className="flex gap-2 text-xs">
              <span className="font-medium text-slate-600">Designation:</span>
              <span className="text-slate-900">{formatKey(details.designation)}</span>
            </div>
          )}
          {details.email && (
            <div className="flex gap-2 text-xs">
              <span className="font-medium text-slate-600">Email:</span>
              <span className="text-slate-900">{details.email}</span>
            </div>
          )}
        </div>
      );
    }

    // Default: show all key-value pairs
    return (
      <div className="space-y-1">
        {renderKeyValue(details, ['previous', 'new'])}
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="audit-logs-container">
      {/* Summary Cards */}
      {summary && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4"
        >
          <Card className="p-3 sm:p-4 bg-white border border-stone-200 shadow-sm" data-testid="audit-summary-total">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-slate-100 rounded-lg">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{summary.total_logs}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">{t('auditLogs.totalEvents') || 'Total Events (30d)'}</p>
              </div>
            </div>
          </Card>
          
          {summary.action_summary?.slice(0, 3).map((item, index) => {
            const style = getActionStyle(item.action);
            const Icon = style.icon;
            return (
              <Card key={index} className="p-3 sm:p-4 bg-white border border-stone-200 shadow-sm">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className={`p-1.5 sm:p-2 rounded-lg ${style.color.split(' ')[0]}`}>
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${style.color.split(' ')[1]}`} />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-slate-900">{item.count}</p>
                    <p className="text-[10px] sm:text-xs text-slate-500 truncate">{style.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </motion.div>
      )}

      {/* Filters and Export */}
      <Card className="p-3 sm:p-4 bg-white border border-stone-200 shadow-sm">
        <div className="flex flex-col md:flex-row gap-3 sm:gap-4 items-start md:items-center justify-between">
          <div className="flex flex-wrap gap-2 sm:gap-3 items-center w-full md:w-auto">
            <div className="relative flex-1 md:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={t('auditLogs.searchPlaceholder') || "Search logs..."}
                value={filters.searchQuery}
                onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                className="pl-10 w-full md:w-64"
                data-testid="audit-search-input"
              />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? "bg-slate-100" : ""}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('auditLogs.filters') || 'Filters'}</span>
            </Button>
          </div>
          
          <div className="flex gap-2 w-full md:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('excel')}
              data-testid="export-excel-btn"
              className="flex-1 md:flex-none"
            >
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Excel</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('pdf')}
              data-testid="export-pdf-btn"
              className="flex-1 md:flex-none"
            >
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">PDF</span>
            </Button>
          </div>
        </div>
        
        {/* Expanded Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-stone-200 grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                {t('auditLogs.actionType') || 'Action Type'}
              </label>
              <Select
                value={filters.action}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, action: value === "all" ? "" : value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <SelectTrigger data-testid="action-filter-select">
                  <SelectValue placeholder={t('auditLogs.allActions') || "All Actions"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auditLogs.allActions') || 'All Actions'}</SelectItem>
                  {actionTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                {t('auditLogs.targetType') || 'Target Type'}
              </label>
              <Select
                value={filters.targetType}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, targetType: value === "all" ? "" : value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <SelectTrigger data-testid="target-filter-select">
                  <SelectValue placeholder={t('auditLogs.allTargets') || "All Targets"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auditLogs.allTargets') || 'All Targets'}</SelectItem>
                  <SelectItem value="official">{t('auditLogs.officials') || 'Officials'}</SelectItem>
                  <SelectItem value="application">{t('auditLogs.applications') || 'Applications'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters({ action: "", targetType: "", searchQuery: "" });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                data-testid="clear-filters-btn"
              >
                {t('auditLogs.clearFilters') || 'Clear Filters'}
              </Button>
            </div>
          </motion.div>
        )}
      </Card>

      {/* Audit Log Table */}
      <Card className="bg-white border border-stone-200 shadow-sm overflow-hidden">
        {/* Desktop: Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="audit-logs-table">
            <thead className="bg-slate-50 border-b border-stone-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  {t('auditLogs.dateTime') || 'Date/Time'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  {t('auditLogs.action') || 'Action'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  {t('auditLogs.performedBy') || 'Performed By'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  {t('auditLogs.target') || 'Target'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  {t('auditLogs.details') || 'Details'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <LoadingOverlay fullPage={false} />
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Shield className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">{t('auditLogs.noLogs') || 'No audit logs found'}</p>
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => {
                  const style = getActionStyle(log.action);
                  const Icon = style.icon;
                  return (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors" data-testid={`audit-log-row-${log.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="text-sm text-slate-900">
                              {format(new Date(log.created_at), "MMM dd, yyyy")}
                            </p>
                            <p className="text-xs text-slate-500">
                              {format(new Date(log.created_at), "HH:mm:ss")}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${style.color.split(' ')[0]}`}>
                            <Icon className={`w-4 h-4 ${style.color.split(' ')[1]}`} />
                          </div>
                          <span className="text-sm font-medium text-slate-700">{style.label}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{log.performed_by_name}</p>
                          <p className="text-xs text-slate-500 capitalize">{log.performed_by_role}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm text-slate-900">{log.target_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500 capitalize">{log.target_type}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          {log.details ? (
                            <details className="cursor-pointer group">
                              <summary className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                                {t('auditLogs.viewDetails') || 'View details'}
                              </summary>
                              <div className="mt-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
                                {formatDetails(log.details, log.action)}
                              </div>
                            </details>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: Card View */}
        <div className="md:hidden">
          {loading ? (
            <div className="px-4 py-12 text-center">
              <LoadingOverlay fullPage={false} />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Shield className="w-10 h-10 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 text-sm">{t('auditLogs.noLogs') || 'No audit logs found'}</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-200">
              {filteredLogs.map((log) => {
                const style = getActionStyle(log.action);
                const Icon = style.icon;
                return (
                  <div key={log.id} className="p-4" data-testid={`audit-log-card-${log.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${style.color.split(' ')[0]}`}>
                          <Icon className={`w-4 h-4 ${style.color.split(' ')[1]}`} />
                        </div>
                        <span className="text-sm font-medium text-slate-700">{style.label}</span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {format(new Date(log.created_at), "MMM dd, HH:mm")}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase">{t('auditLogs.performedBy') || 'By'}</p>
                        <p className="text-slate-900 truncate">{log.performed_by_name}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase">{t('auditLogs.target') || 'Target'}</p>
                        <p className="text-slate-900 truncate">{log.target_name || 'N/A'}</p>
                      </div>
                    </div>
                    
                    {log.details && (
                      <details className="cursor-pointer mt-2">
                        <summary className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                          {t('auditLogs.viewDetails') || 'View details'}
                        </summary>
                        <div className="mt-2 bg-slate-50 p-2 rounded-lg border border-slate-200">
                          {formatDetails(log.details, log.action)}
                        </div>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <SharedPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.total}
            pageSize={pagination.limit}
            onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
            variant="minimal"
            position="bottom"
            className="px-4 sm:px-6 py-3 sm:py-4 border-t border-stone-200"
          />
        )}
      </Card>
    </div>
  );
}
